//フォルダー削除画面
import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { deleteBook } from '../redux/bookDbSlice';
import { deleteSheet } from '../redux/sheetDbSlice';
import { deleteSheetInformation } from '../redux/sheetInformationDbSlice';
import { deleteCard } from '../redux/cardDbSlice';
import { deleteCardAttribute } from '../redux/cardAttributeDbSlice';
import { deleteCardShape } from '../redux/cardShapeDbSlice';
import { deleteMemoCardContent } from '../redux/memoCardContentDbSlice';
import { deleteUiCardItem } from '../redux/uiCardItemDbSlice';
import { deleteDrawCardContent } from '../redux/drawCardContentDbSlice';
import { deleteArrow } from '../redux/arrowDbSlice';
import { deleteSheetTree } from '../redux/sheetTreeDbSlice';
import { deleteRelationUserBook } from '../redux/relationUserBookDbSlice';
import { deleteRelationFolderBook } from '../redux/relationFolderBookDbSlice';
import { deleteFolder, setCurrentFolder } from '../redux/folderDbSlice';
import { deleteRelationUserFolder } from '../redux/relationUserFolderDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';

import useAllBookDbsInFolder from '../hooks/useAllBookDbsInFolder';
import useAllSheetsDbByBooks from '../hooks/useAllSheetsDbByBooks';
import useAllSheetInformationIdsBySheetIds from '../hooks/useAllSheetInformationIdsBySheetIds';
import useAllCardDbsBySheetIds from '../hooks/useAllCardDbsBySheetIds';
import useCardAttributeIdsByCards from '../hooks/useCardAttributeIdsByCards';
import useCardShapeIdsByCards from '../hooks/useCardShapeIdsByCards';
import useMemoCardContentIdsByCards from '../hooks/useMemoCardContentIdsByCards';
import useUiCardItemIdsByCards from '../hooks/useUiCardItemIdsByCards';
import useDrawCardContentIdsByCards from '../hooks/useDrawCardContentIdsByCards';
import useArrowIdsBySheets from '../hooks/useArrowIdsBySheets';
import useRUFsDb from '../hooks/useRUFsDb';
import useAllRelationUserBookIdsByBooks from '../hooks/useAllRelationUserBookIdsByBooks';
import useAllRelationFolderBookIdsByBooks from '../hooks/useAllRelationFolderBookIdsByBooks';
import useAllSheetTreeIdsByBooks from '../hooks/useAllSheetTreeIdsByBooks';
import useOneRUFDbByFolder from '../hooks/useOneRUFDbByFolder';
import useCurrentFolderId from '../hooks/useCurrentFolderId';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useUserCondition from '../hooks/useUserCondition';
import { deepCopy } from '../general';

type DeleteFolderDialogProps = {
  open: boolean;
  onClose: () => void;
  updateOrder: (id: string) => void;
}
const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({open, onClose, updateOrder}) => {
  const dispatch = useDispatch();
  const currentFolderId = useCurrentFolderId();
  const [, rufs] = useRUFsDb();

  const [bookIds] = useAllBookDbsInFolder(currentFolderId);
  const [sheetIds] = useAllSheetsDbByBooks(bookIds);
  const sheetInformationIds = useAllSheetInformationIdsBySheetIds(sheetIds);
  const [cardIds] = useAllCardDbsBySheetIds(sheetIds);
  const cardAttributeIds = useCardAttributeIdsByCards(cardIds);
  const cardShapeIds = useCardShapeIdsByCards(cardIds);
  const memoContentIds = useMemoCardContentIdsByCards(cardIds);
  const uiItemIds = useUiCardItemIdsByCards(cardIds);
  const drawContentIds = useDrawCardContentIdsByCards(cardIds);
  const arrowIds = useArrowIdsBySheets(sheetIds);
  const rubIds = useAllRelationUserBookIdsByBooks(bookIds);
  const rfbIds = useAllRelationFolderBookIdsByBooks(bookIds);
  const sheetTreeIds = useAllSheetTreeIdsByBooks(bookIds);
  const [rufId, ruf] = useOneRUFDbByFolder(currentFolderId);
  const userId = useCurrentUserId();
  const [userConditionId, userCondition] = useUserCondition(userId);

  const updateZoomRates = <T extends UserCondition | undefined>(condition: T, targetId: string): T => {
    if (!condition) return condition;
    const idsInZoomRates = Object.keys(condition.zoomRates);
    if (idsInZoomRates.includes(targetId)) {
      const newCondition = deepCopy(condition);
      delete newCondition.zoomRates[targetId];
      return newCondition;
    } else return condition;
  }

  const deleteFolderExe = () => {
    if (currentFolderId && (currentFolderId !== '')) {
      updateOrder(currentFolderId);
      const newCondition = deepCopy(userCondition);
      sheetIds.forEach(id => {
        if (newCondition) newCondition.zoomRates = updateZoomRates(newCondition, id).zoomRates;
        dispatch(deleteSheet({id: id}));
      });// Folder中のシートを削除する
      if (userConditionId && newCondition) dispatch(updateUserCondition({ id: userConditionId, zoomRates: newCondition.zoomRates }));
      sheetInformationIds.forEach(id => dispatch(deleteSheetInformation({id: id})));
      cardIds.forEach(id => dispatch(deleteCard({id: id})));// Folder中のカードを削除する
      cardAttributeIds?.forEach(id => dispatch(deleteCardAttribute({id: id})));
      cardShapeIds?.forEach(id => dispatch(deleteCardShape({id: id})));
      memoContentIds?.forEach(id => dispatch(deleteMemoCardContent({id: id})));
      uiItemIds?.forEach(id => dispatch(deleteUiCardItem({id: id})));
      drawContentIds?.forEach(id => dispatch(deleteDrawCardContent({id: id})));
      arrowIds?.forEach(id => dispatch(deleteArrow({id: id})));
      sheetTreeIds.forEach(id => dispatch(deleteSheetTree({id: id})));
      rfbIds.forEach(id => dispatch(deleteRelationFolderBook({id: id})));
      rubIds.forEach(id => dispatch(deleteRelationUserBook({id: id})));
      bookIds.forEach(id => dispatch(deleteBook({id: id})));// Folder中のBookを削除する
      if (rufId) dispatch(deleteRelationUserFolder({id: rufId}));
      dispatch(deleteFolder({id: currentFolderId}));// Folderを削除する

      let newSelectId = '';
      if (!ruf) newSelectId = '';
      else if (ruf.order === 0) newSelectId = rufs.find(target => target.order === 1)?.folderId || '';
      else newSelectId = rufs.find(target => target.order === ruf.order - 1)?.folderId || '';

      dispatch(setCurrentFolder(newSelectId));
    }
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>このフォルダを削除します</DialogTitle>
      <DialogContent />
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button color='primary' onClick={() => deleteFolderExe()}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteFolderDialog;
